import {Capacitor, registerPlugin} from '@capacitor/core'
import type {PluginListenerHandle} from '@capacitor/core'

interface IProgressStatus {
  type: 'download' | 'upload'
  url: string
  bytes: number
  contentLength: number
  lengthComputable: boolean
}

interface IUploadFileOptions {
  url: string
  path: string
  mimeType?: string
  fileKey?: string
  progress?: boolean
  method?: string
  params?: Record<string, string | string[]>
  headers?: Record<string, string>
  readTimeout?: number
  connectTimeout?: number
}

interface IUploadFileResult {
  bytesSent: number
  responseCode: string
  response?: string
}

interface IDownloadFileOptions {
  url: string
  path: string
  progress?: boolean
  headers?: Record<string, string>
  readTimeout?: number
  connectTimeout?: number
}

interface IDownloadFileResult {
  path?: string
}

interface IFileTransferPlugin {
  uploadFile(options: IUploadFileOptions): Promise<IUploadFileResult>
  downloadFile(options: IDownloadFileOptions): Promise<IDownloadFileResult>
  addListener(eventName: 'progress', listenerFunc: (progress: IProgressStatus) => void): Promise<PluginListenerHandle>
}

export interface INativeUploadProgressEvent {
  loaded: number
  total: number
  lengthComputable: boolean
}

export interface INativeUploadRequest {
  url: string
  path: string
  mimeType: string
  fileKey: string
  getHeaders: () => Promise<Record<string, string>>
  onProgress?: (progressEvent: INativeUploadProgressEvent) => void
}

export interface INativeUploadFailure {
  message: string
  httpStatus?: number
  body?: string
}

// The server finishes streaming the body to object storage AFTER the last byte
// arrives, so the response can lag well past the plugin's 60s default.
const UPLOAD_TIMEOUT_MS = 30 * 60 * 1000
const CONNECT_TIMEOUT_MS = 60 * 1000

const FileTransferPlugin = registerPlugin<IFileTransferPlugin>('FileTransfer')

export function isFileTransferAvailable(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable('FileTransfer')
}

export function getNativeUploadFailure(error: unknown): INativeUploadFailure {
  const data = (error as {data?: {httpStatus?: number, body?: string, message?: string}})?.data
  const message = (error as Error)?.message ?? data?.message ?? 'Native upload failed'
  return {
    message,
    httpStatus: data?.httpStatus,
    body: data?.body,
  }
}

// Progress events only carry the target url, so uploads run one at a time to
// keep progress attribution unambiguous.
let uploadQueue: Promise<unknown> = Promise.resolve()

export function uploadFileNatively(request: INativeUploadRequest): Promise<string | undefined> {
  const run = async (): Promise<string | undefined> => {
    let progressListener: PluginListenerHandle | null = null
    const startedAt = Date.now()
    try {
      if (request.onProgress) {
        progressListener = await FileTransferPlugin.addListener('progress', (progress) => {
          if (progress.type !== 'upload' || progress.url !== request.url) {
            return
          }
          request.onProgress({
            loaded: progress.bytes,
            total: progress.contentLength,
            lengthComputable: progress.lengthComputable,
          })
        })
      }
      const headers = await request.getHeaders()
      const result = await FileTransferPlugin.uploadFile({
        url: request.url,
        path: request.path,
        mimeType: request.mimeType,
        fileKey: request.fileKey,
        method: 'POST',
        progress: !!request.onProgress,
        headers,
        readTimeout: UPLOAD_TIMEOUT_MS,
        connectTimeout: CONNECT_TIMEOUT_MS,
      })
      const elapsedMs = Date.now() - startedAt
      const mbps = result.bytesSent ? ((result.bytesSent / (1024 * 1024)) / (elapsedMs / 1000)).toFixed(2) : '?'
      return result.response
    } catch (error) {
      const failure = getNativeUploadFailure(error)
      throw error
    } finally {
      await progressListener?.remove()
    }
  }
  const queuedRun = uploadQueue.then(run, run)
  uploadQueue = queuedRun.catch(() => undefined)
  return queuedRun
}

export interface INativeDownloadRequest {
  url: string
  path: string
  onProgress?: (progressEvent: INativeUploadProgressEvent) => void
}

// Same single-flight rule as uploads: progress events only carry the url.
let downloadQueue: Promise<unknown> = Promise.resolve()

export function downloadFileNatively(request: INativeDownloadRequest): Promise<void> {
  const run = async (): Promise<void> => {
    let progressListener: PluginListenerHandle | null = null
    try {
      if (request.onProgress) {
        progressListener = await FileTransferPlugin.addListener('progress', (progress) => {
          if (progress.type !== 'download' || progress.url !== request.url) {
            return
          }
          request.onProgress({
            loaded: progress.bytes,
            total: progress.contentLength,
            lengthComputable: progress.lengthComputable,
          })
        })
      }
      await FileTransferPlugin.downloadFile({
        url: request.url,
        path: request.path,
        progress: !!request.onProgress,
        readTimeout: UPLOAD_TIMEOUT_MS,
        connectTimeout: CONNECT_TIMEOUT_MS,
      })
    } finally {
      await progressListener?.remove()
    }
  }
  const queuedRun = downloadQueue.then(run, run)
  downloadQueue = queuedRun.catch(() => undefined)
  return queuedRun
}
